import { CalendarDays } from "lucide-react";

import { Image } from "@/components/image";
import { Link } from "@/components/link";
import { Markdown } from "@/components/page-editor-preview";
import { Button } from "@/components/ui/button";
import type { Page } from "@/lib/types";

/**
 * Each section pulls its body from a CMS page of the given name, so directors
 * can edit the copy in Admin → Pages. The fallback renders until that page
 * exists.
 */
const SPLIT_SECTIONS = [
  {
    fallback:
      "<p>OSCAR is our school's student-run radio station. Every show you hear, from the music picks to the interviews, is planned, presented and produced by students, with a little help from staff supervisors. It's a place to learn broadcasting, find your voice, and have a lot of fun on air.</p>",
    heading: "What is OSCAR?",
    image: {
      alt: "Students presenting live in the OSCAR studio",
      src: "/img/20170222-OSCAR-Radio-001-scaled.jpg",
    },
    pageName: "what-is-oscar",
  },
  {
    fallback:
      "<p>OSCAR started as a lunchtime experiment with a single microphone and a borrowed mixing desk. Over the years it has grown into a fully equipped studio broadcasting a weekly schedule of shows, with generations of students passing the headphones on to the next.</p>",
    heading: "The history of OSCAR",
    image: { alt: "Vintage radio equipment", src: "/img/radio.jpg" },
    pageName: "history-of-oscar",
  },
];

const LISTEN_FALLBACK =
  "<p>We broadcast live during term time. Missed a show? Every episode is recorded and available on demand in the archive, so you can listen back whenever suits you.</p>";

const contentFor = (pages: Page[], name: string, fallback: string) =>
  pages.find((it) => it.name === name)?.content ?? fallback;

export function AboutSections({ pages }: { pages: Page[] }) {
  return (
    <div className="flex flex-col gap-16">
      {SPLIT_SECTIONS.map((section, index) => (
        <section
          className="grid items-center gap-8 lg:grid-cols-2 lg:gap-14"
          key={section.pageName}
        >
          <div className={index % 2 === 1 ? "lg:order-last" : undefined}>
            <Image
              alt={section.image.alt}
              className="aspect-[4/3] w-full rounded-2xl object-cover outline -outline-offset-1 outline-black/10 dark:outline-white/10"
              height={480}
              sizes="(min-width: 1024px) 40rem, 100vw"
              src={section.image.src}
              width={640}
            />
          </div>
          <div>
            <h2 className="mb-4 text-3xl font-extrabold tracking-tight">
              {section.heading}
            </h2>
            <Markdown
              content={contentFor(pages, section.pageName, section.fallback)}
            />
          </div>
        </section>
      ))}

      {/* A different layout family from the splits above: full-width band with
          a schedule CTA, so the page doesn't zigzag three times in a row. */}
      <section className="rounded-2xl bg-secondary px-6 py-12 sm:px-10 lg:px-14">
        <div className="mx-auto flex max-w-3xl flex-col items-start gap-6">
          <h2 className="text-3xl font-extrabold tracking-tight">
            When can I listen?
          </h2>
          <Markdown
            content={contentFor(pages, "when-can-i-listen", LISTEN_FALLBACK)}
          />
          <Link href="/broadcastSchedule">
            <Button>
              <CalendarDays /> See today&apos;s schedule
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
